import { useEffect, useState } from 'react';
import { useAtom, useAtomValue } from 'jotai';
import { Brain, Target, X } from '@phosphor-icons/react';
import CoachMetrics from '../../shared/components/CoachMetrics';
import { drumRecordsAtom, drumLanguageAtom, drumUserTokenAtom } from '../../shared/store/drumAppAtoms';
import type { PlayRecord } from '../../shared/data/constants';
import { DRUMS } from '../../shared/data/constants';
import { fetchPlayRecords } from '../../shared/api/memberApi';
import { formatProfileDate, getDrumDisplayName } from '../../shared/utils/drumHelpers';
import { useDialogFocus } from '../../shared/hooks/useDialogFocus';
import './Records.scss';

function formatPracticeTime(durationMs: number, language: string) {
  const totalMinutes = Math.max(0, Math.round(durationMs / 60000));
  if (totalMinutes < 60) return language === 'ko' ? `${totalMinutes}분` : `${totalMinutes}m`;
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (language === 'ko') return minutes ? `${hours}시간 ${minutes}분` : `${hours}시간`;
  return minutes ? `${hours}h ${minutes}m` : `${hours}h`;
}

function withPreviousScores(records: PlayRecord[]) {
  return records.map((record, index) => {
    const previous = records.slice(index + 1).find((item) => (
      (record.scoreId && item.scoreId === record.scoreId && item.difficulty === record.difficulty)
      || (!record.scoreId && item.title === record.title && item.difficulty === record.difficulty)
    ));
    return previous ? { ...record, previousScore: previous.score } : record;
  });
}

function buildCoachMetrics(record: PlayRecord) {
  const accuracy = Math.max(0, Math.min(100, Number(record.accuracy) || 0));
  const misses = Object.values(record.mistakes || {}).reduce((sum, value) => sum + value, 0);
  const missPenalty = Math.min(12, misses);
  return {
    beatStability: accuracy,
    timingControl: accuracy,
    patternAccuracy: Math.max(0, accuracy - missPenalty),
    tempoMaintenance: accuracy,
    restRecovery: accuracy,
    limbCoordination: Math.max(0, accuracy - Math.ceil(missPenalty / 2)),
  };
}

function buildCoachLines(record: PlayRecord, language: string) {
  const topMistake = Object.entries(record.mistakes || {}).sort((first, second) => second[1] - first[1])[0];
  if (language === 'ko') {
    const lines = [
      record.accuracy >= 90 ? '정확도가 높게 유지된 연주입니다.' : '정확도를 조금 더 끌어올리면 기록이 안정됩니다.',
      `최대 콤보 ${record.maxCombo.toLocaleString()}를 만들었습니다.`,
    ];
    if (topMistake && topMistake[1] > 0) {
      lines.push(`${getDrumDisplayName(topMistake[0], language)}에서 ${topMistake[1]}회 실수가 있었습니다.`);
    }
    return lines;
  }
  const lines = [
    record.accuracy >= 90 ? 'Accuracy stayed high through this play.' : 'Focus on accuracy to make the next record more consistent.',
    `You reached a max combo of ${record.maxCombo.toLocaleString()}.`,
  ];
  if (topMistake && topMistake[1] > 0) {
    lines.push(`${getDrumDisplayName(topMistake[0], language)} had ${topMistake[1]} mistakes.`);
  }
  return lines;
}

function Records() {
  const [records, setRecords] = useAtom(drumRecordsAtom);
  const language = useAtomValue(drumLanguageAtom);
  const token = useAtomValue(drumUserTokenAtom);
  const [selected, setSelected] = useState<PlayRecord | null>(null);
  const [isLoading, setIsLoading] = useState(Boolean(token));
  const [errorMessage, setErrorMessage] = useState('');
  const detailDialogRef = useDialogFocus(Boolean(selected), () => setSelected(null));

  useEffect(() => {
    if (!token) {
      setRecords([]);
      setIsLoading(false);
      setErrorMessage('');
      return undefined;
    }
    let cancelled = false;
    setIsLoading(true);
    fetchPlayRecords(token)
      .then((items) => {
        if (cancelled) return;
        setRecords(withPreviousScores(items));
        setErrorMessage('');
      })
      .catch((error) => {
        if (cancelled) return;
        setRecords([]);
        setErrorMessage(error instanceof Error ? error.message : (language === 'ko' ? '연주 기록을 불러오지 못했습니다.' : 'Unable to load records.'));
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token, language, setRecords]);

  const totalAccuracy = Math.round(records.reduce((sum, record) => sum + record.accuracy, 0) / Math.max(1, records.length));
  const bestCombo = Math.max(0, ...records.map((record) => record.maxCombo));
  const practiceMs = records.reduce((sum, record) => sum + (record.durationMs || 0), 0);
  const emptyMistakes = Object.fromEntries(DRUMS.map(([name]) => [name, 0]));
  const detailStats = selected?.stats || { Great: 0, Good: 0, Bad: 0, Miss: 0 };
  const detailMistakes = selected?.mistakes || emptyMistakes;
  const previousScore = selected?.previousScore;
  const scoreDelta = previousScore != null ? (selected?.score || 0) - previousScore : null;
  const maxMistakes = Math.max(1, ...Object.values(detailMistakes));
  const detailCoachMetrics = selected ? buildCoachMetrics(selected) : {};
  const coachLines = selected ? buildCoachLines(selected, language) : [];
  const emptyCopy = errorMessage
    || (isLoading
      ? (language === 'ko' ? '연주 기록을 불러오는 중...' : 'Loading records...')
      : !token
        ? (language === 'ko' ? '로그인하면 연주 기록을 확인할 수 있습니다.' : 'Sign in to view your play records.')
        : (language === 'ko' ? '연주 기록이 없습니다.' : 'No play records yet.'));

  return (
    <div className="page records-page">
      <div className="page-intro"><span className="eyebrow">YOUR PROGRESS</span><h1>Performance Records</h1><p>Review play results and see which rhythm skills are improving.</p></div>
      <div className="record-summary">
        <div><span>Total Plays</span><strong>{records.length}</strong></div>
        <div><span>Average Accuracy</span><strong>{records.length ? `${totalAccuracy}%` : '-'}</strong></div>
        <div><span>Best Combo</span><strong>{records.length ? bestCombo : '-'}</strong></div>
        <div><span>Practice Time</span><strong>{formatPracticeTime(practiceMs, language)}</strong></div>
      </div>
      <section className="records-table">
        <header><span>Song</span><span>Level</span><span>Score</span><span>Rank</span><span>Accuracy</span><span>Date</span><span>{language === 'ko' ? '상세' : 'Details'}</span></header>
        {records.length ? records.map((record) => (
          <div className="record-row" key={record.playId}>
            <span>{record.title}</span><span>{record.difficulty}</span><span>{record.score.toLocaleString()}</span>
            <span className="record-rank">{record.rank}</span><span>{record.accuracy}%</span><span>{formatProfileDate(record.playedAt, language) || record.playedAt}</span>
            <button type="button" className="record-detail-button" onClick={() => setSelected(record)}>{language === 'ko' ? '상세보기' : 'View details'}</button>
          </div>
        )) : (
          <div className="record-row empty-list">{emptyCopy}</div>
        )}
      </section>
      {selected && (
        <section ref={detailDialogRef as React.RefObject<HTMLElement>} className="record-detail" role="dialog" aria-modal="true" aria-labelledby="record-detail-title" tabIndex={-1}>
          <button className="record-detail-close" onClick={() => setSelected(null)} aria-label={language === 'ko' ? '닫기' : 'Close'}><X /></button>
          <header className="record-detail__header">
            <div className="record-detail__title"><span>PLAY DETAIL</span><h2 id="record-detail-title">{selected.title}</h2><p>{selected.difficulty} · {formatProfileDate(selected.playedAt, language) || selected.playedAt}</p></div>
            <div className="record-detail__rank"><strong>{selected.rank}</strong><small>{language === 'ko' ? `정확도 ${selected.accuracy}%` : `${selected.accuracy}% ACCURACY`}</small></div>
            <div className="record-detail__score"><span>{language === 'ko' ? '최종 점수' : 'FINAL SCORE'}</span><strong>{selected.score.toLocaleString()}</strong>{scoreDelta != null && previousScore != null ? <small>{language === 'ko' ? '이전 점수' : 'PREVIOUS'} {previousScore.toLocaleString()} <b>{scoreDelta >= 0 ? '+' : ''}{scoreDelta.toLocaleString()}</b></small> : null}</div>
          </header>
          <div className="record-detail__judgments">
            {(['Great', 'Good', 'Bad', 'Miss'] as const).map((item) => (
              <div className={item.toLowerCase()} key={item}><span>{item}</span><strong>{detailStats[item]}</strong></div>
            ))}
            <div><span>{language === 'ko' ? '최대 콤보' : 'Max Combo'}</span><strong>{selected.maxCombo}</strong></div>
            <div><span>{language === 'ko' ? '정확도' : 'Accuracy'}</span><strong>{selected.accuracy}%</strong></div>
          </div>
          <div className="record-detail__body">
            <section className="record-detail__coach">
              <div className="record-detail__section-title"><Brain weight="fill" /><div><span>COACH REVIEW</span><h3>{language === 'ko' ? (selected.accuracy >= 90 ? '안정적인 타이밍으로 성장하고 있어요.' : '다음 연주에서 정확도를 더 끌어올려 보세요.') : (selected.accuracy >= 90 ? 'Strong timing, steady progress.' : 'Use the next play to tighten accuracy.')}</h3></div></div>
              <CoachMetrics metrics={detailCoachMetrics} language={language} className="record-detail__coach-metrics" />
              <ul className="record-detail__coach-lines">{coachLines.map((line) => <li key={line}>{line}</li>)}</ul>
            </section>
            <section className="record-detail__analysis">
              <div className="record-detail__section-title"><Target weight="fill" /><div><span>PLAY ANALYSIS</span><h3>{language === 'ko' ? '자주 틀린 악기' : 'Most Missed Instruments'}</h3></div></div>
              <p>{language === 'ko' ? '이번 연주에서 악기별 실수 횟수를 확인하세요.' : 'Review instrument mistakes from this play.'}</p>
              <div className="record-detail__mistakes">
                {DRUMS.map(([name, color]) => {
                  const count = detailMistakes[name] || 0;
                  return (
                    <div className="record-detail__mistake-row" key={name}>
                      <div><strong>{getDrumDisplayName(name, language)}</strong><b>{language === 'ko' ? `${count}회 실수` : `${count} mistakes`}</b></div>
                      <span><i style={{ width: `${Math.max(8, (count / maxMistakes) * 100)}%`, background: color }} /></span>
                    </div>
                  );
                })}
              </div>
            </section>
          </div>
        </section>
      )}
    </div>
  );
}

export default Records;
